import React, { useState, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Button, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import ErrorIcon from '@mui/icons-material/Error';
import logoImage from "../../../assets/images/logo.png";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import _ from "lodash";
import callApi from "../../../services/callApi";
import Loader from "../../../components/Loader/Loader";

export default function RutPagador() {
  const navigate = useNavigate();
  const location = useLocation();
  const MySwal = withReactContent(Swal);
  const [rut, setRut] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [titulo, setTitulo] = useState("Nombre Profesional");
  const [profesion, setProfesion] = useState("Profesion");
  const [servicio_nombre, setServicioNombre] = useState("Nombre Servicio");
  const [servicio_precios, setServicioPrecios] = useState("Valor servicio");
  const [servicio_modalidad, setServicioModalidad] = useState("Modalidad");
  const [fecha_formateada, setFechaFormateada] = useState("");
  const [hourss, setHourss] = useState("");
  const [transferenciaInfo, setTransferenciaInfo] = useState(null);

  const [isValidating, setIsValidating] = useState(false);
  const [isRutValidated, setIsRutValidated] = useState(false);
  const [wasValidated, setWasValidated] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);

  const currentRutRef = useRef(rut);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (location.state && location.state.transferencia_id) {
      obtenerInfoTransferencia(location.state.transferencia_id);
    }
  }, [location.state]);

  useEffect(() => {
    currentRutRef.current = rut;
  }, [rut]);

  useEffect(() => {
    if (rut.length >= 8) {
      validateRut();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [rut]);

  const obtenerInfoTransferencia = async (transferencia_id) => {
    setIsLoading(true);
    try {
      const response = await callApi(
        `/transferencia/transferenciaInformacion?transferencia_id=${transferencia_id}`,
        "get"
      );

      if (response && response.transferencia) {
        const transf = response.transferencia;
        setTransferenciaInfo(transf);

        if (transf.profesional_nombres && transf.profesional_apellidos) {
          setTitulo(
            `${transf.profesional_nombres} ${transf.profesional_apellidos}`
          );
        }
        if (transf.profesional_profesion)
          setProfesion(transf.profesional_profesion);
        if (transf.servicio_nombre) setServicioNombre(transf.servicio_nombre);
        if (transf.servicio_precio) {
          const precio = new Intl.NumberFormat("es-CL", {
            style: "currency",
            currency: "CLP",
            minimumFractionDigits: 0,
          }).format(transf.servicio_precio);
          setServicioPrecios(precio);
        }
        if (transf.servicio_modalidad)
          setServicioModalidad(transf.servicio_modalidad);
        if (transf.fecha_agenda)
          setFechaFormateada(transf.fecha_agenda_corta || transf.fecha_agenda);
        if (transf.hora_agenda) setHourss(transf.hora_agenda);
      } else {
        console.error("No se recibió respuesta válida de la API");
        MySwal.fire({
          title: "Error",
          text: "No se pudo obtener la información de la transferencia",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error al obtener información de transferencia:", error);
      MySwal.fire({
        title: "Error",
        text: "No se pudo obtener la información de la transferencia",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRutChange = (event) => {
    const value = event.target.value;
    const formattedRut = formatRut(value);
    setRut(formattedRut);

    if (wasValidated) {
      setIsRutValidated(false);
      setIsValid(false);
      setIsInvalid(false);
    }

    if (formattedRut.length >= 8) {
      debouncedValidateRut(formattedRut);
    }
  };

  const formatRut = (rut) => {
    let cleanRut = rut.replace(/[^0-9kK]+/g, "").toUpperCase();

    if (cleanRut.length > 1) {
      const body = cleanRut.slice(0, -1);
      const dv = cleanRut.slice(-1);

      const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

      cleanRut = formattedBody + "-" + dv;
    }

    return cleanRut;
  };

  const debouncedValidateRut = _.debounce(async (rutToValidate) => {
    if (rutToValidate !== currentRutRef.current) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setIsValidating(true);
    setIsInvalid(false);

    try {
      const rutLimpio = rutToValidate.trim();
      const url = `/transferencia/validarRUT?rut=${rutLimpio}`;
      const response = await callApi(url, "get", null, {
        signal: abortControllerRef.current.signal,
      });

      if (rutToValidate === currentRutRef.current) {
        setWasValidated(true);

        if (response?.valido) {
          setIsRutValidated(true);
          setIsValid(true);
          setIsInvalid(false);
        } else {
          setIsRutValidated(false);
          setIsValid(false);
          setIsInvalid(true);
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      console.error("Error al validar RUT:", error);
      if (rutToValidate === currentRutRef.current) {
        setIsRutValidated(false);
        setIsValid(false);
        setIsInvalid(true);
      }
    } finally {
      if (rutToValidate === currentRutRef.current) {
        setIsValidating(false);
      }
    }
  }, 300); // Esperar 300ms entre llamadas

  const validateRut = () => {
    debouncedValidateRut(currentRutRef.current);
  };

  const getButtonColor = () => {
    if (isInvalid) return "#E36914";
    if (isRutValidated) return "#8753b1";
    return "#8753b1";
  };

  const getButtonHoverColor = () => {
    if (isInvalid) return "#B54E0F";
    if (isRutValidated) return "#651d87";
    return "#651d87";
  };

  const getButtonText = () => {
    if (isValidating) return "";
    if (isInvalid) return "Inválido";
    if (isRutValidated) return "OK";
    return "Validar";
  };

  const handleTransferir = async () => {
    setIsLoading(true);
    try {
      const response = await callApi(
        `/transferencia/transferenciaRegistrarRUT`,
        "post",
        {
          transferencia_id: location.state.transferencia_id,
          rut_pagador: rut.trim(),
        }
      );

      if (response && response.rut_asignado) {
        if (response.estado_pago) {
          MySwal.fire({
            title: "Información",
            text: "Esta transferencia ya ha sido pagada",
            icon: "info",
          });
          return;
        }

        // Si hay que redireccionar, mostramos mensaje y no continuamos
        if (response.redireccionar) {
          MySwal.fire({
            title: "Información",
            text:
              response.message || "Este RUT ya está asignado a otro pagador",
            icon: "info",
          });
          return;
        }

        // Si todo está bien, continuamos al siguiente paso
        navigate("/pro/transferencia/datos_transferencia", {
          state: {
            ...location.state,
            rutPagador: rut,
            transferenciaInfo: transferenciaInfo,
          },
        });
      } else {
        // Si hay algún error en la respuesta
        MySwal.fire({
          title: "Error",
          text: response?.message || "No se pudo registrar el RUT del pagador",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error al registrar RUT del pagador:", error);
      MySwal.fire({
        title: "Error",
        text: "No se pudo procesar la solicitud. Intente nuevamente.",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const transformModalidad = (modalidad) => {
    switch (modalidad) {
      case "consultorio":
        return "Consulta presencial";
      case "domicilio":
        return "Consulta a domicilio";
      case "online":
        return "Consulta online";
      default:
        return modalidad;
    }
  };

  const formatearFechaSinAnio = (fecha) => {
    if (!fecha) return "";
    return fecha.slice(0, -5);
  };

  const isValidRut = (rut) => {
    return isRutValidated || rut.trim().length >= 9;
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />
      
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 2,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        <Typography sx={{ 
          fontFamily: "Quicksand-Regular",
          color: "#626262",
          fontSize: { xs: "14px", md: "16px" },
          mb: 0
        }}>
          Estas agendando con:
        </Typography>
        <Typography
          sx={{
            mt: -0.5,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "18px", md: "20px" },
            color: "#191C1F",
            mb: 0
          }}
        >
          {titulo}
        </Typography>
        <Typography
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            mt: -0.5,
            fontSize: { xs: "14px", md: "16px" },
            color: "default",
            mb: 0
          }}
        >
          {profesion}
        </Typography>
        <Typography sx={{ 
          fontFamily: "Quicksand-Regular",
          color: "#626262",
          mt: 2,
          fontSize: { xs: "14px", md: "16px" },
          mb: 0
        }}>
          El servicio de:
        </Typography>
        <Typography
          sx={{
            mt: -0.5,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "18px", md: "20px" },
            color: "#191C1F",
            mb: 0
          }}
        >
          {servicio_nombre}
        </Typography>
        <Typography
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            mt: -0.5,
            fontSize: { md: "16px", xs: "14px" },
            color: "#191C1F",
            mb: 0
          }}
        >
          {servicio_precios}
        </Typography>
        <Box
          sx={{
            display: "inline-block",
            backgroundColor: "#f0f4ff",
            color: "#3B82F6",
            fontFamily: "Quicksand-Medium",
            fontSize: { md: "15px", xs: "13px" },
            borderRadius: 2,
            paddingLeft: 0.75,
            paddingRight: 0.75,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            marginLeft: -0.75,
            mt: -0.75,
            textAlign: "left",
          }}
        >
          {transformModalidad(servicio_modalidad)}
        </Box>
        {fecha_formateada && (
          <Typography
            sx={{
              mt: 0,
              color: "#007FFF",
              fontFamily: "Quicksand-Bold",
              fontSize: { md: "16px", xs: "14px" },
            }}
          >
            {formatearFechaSinAnio(fecha_formateada)} {hourss ? "-" : ""}{" "}
            {hourss}
          </Typography>
        )}
      </Box>

      <Typography
        sx={{
          fontFamily: "Quicksand-Bold",
          fontSize: { md: "24px", xs: "20px" },
          textAlign: "center",
          color: "#191C1F",
          mt: 3,
          mb: 1
        }}
      >
        Datos del Paciente
      </Typography>

      <Box
        translate="no"                    // ← AGREGAR
        className="notranslate"           // ← AGREGAR
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "white",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", md: "66.67%" },
            mx: "auto",
          }}
        >
          <Typography
            sx={{
              mt: 3,
              mb: 1,
              fontFamily: "Quicksand-Regular",
              fontSize: { md: "16px", xs: "14px" },
              color: "#191C1F",
              textAlign: "center",
            }}
          >
          Ingresa el RUT del dueño de la cuenta bancaria con la que transferirás el pago de tu sesión
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                pt: 1,
                fontFamily: isValidRut(rut)
                  ? "Quicksand-Bold"
                  : "Quicksand-Regular",
                fontSize: { md: "16px", xs: "14px" },
                color: isValidRut(rut) ? "#2669A8" : "#191C1F",
              }}
            >
              {isValidRut(rut) ? "RUT" : "RUT*"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: 1,
              }}
            >
              <Box
                translate="no"                    // ← AGREGAR
                className="notranslate"           // ← AGREGAR
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  border: "1px solid #909090",
                  backgroundColor: isRutValidated ? "#f5f5f5" : "white",
                  opacity: isRutValidated ? 0.6 : 1,
                  p: 0.5,
                  "&:focus-within": {
                    borderColor: "#3B82F6",
                    boxShadow: "0 0 0 1px #3B82F6",
                  },
                }}
              >
                <input
                  id="rut"
                  name="rut"
                  onChange={handleRutChange}
                  value={rut}
                  maxLength={12}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  style={{
                    border: "none",
                    width: "100%",
                    outline: "none",
                    background: "transparent",
                    fontFamily: "Quicksand-Regular",
                    color: "#4F4F4F",
                    fontSize: "16px",
                    padding: 0,
                  }}
                  placeholder="Ej: 12.345.678-9"
                />
              </Box>
            </Box>

            {/* Alerta de RUT inválido */}
            {isInvalid && (
              <Box
                translate="no"                    // ← AGREGAR
                className="notranslate"           // ← AGREGAR
                component="form"
                autoComplete="off"
                sx={{
                  backgroundColor: "#FFEBEE",
                  borderRadius: "4px",
                  padding: "8px",
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <ErrorIcon sx={{ color: "#C62828", fontSize: "20px" }} />
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Bold",
                      fontSize: { md: "15px", xs: "13px" },
                      lineHeight: 1.2,
                      color: "#C62828",
                    }}
                  >
                    Identificación inválida
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Regular",
                      fontSize: { md: "12px", xs: "11px" },
                      lineHeight: 1.2,
                      color: "#C62828",
                    }}
                  >
                    Por favor intenta nuevamente
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Button
            translate="no"
            variant="contained"
            disableRipple
            disableElevation
            disableFocusRipple
            disabled={!isRutValidated || isLoading}
            onClick={handleTransferir}
            sx={{
              fontSize: { xs: "14px", md: "16px", lg: "16px" },
              fontFamily: "Quicksand-Bold",
              backgroundColor: "#3B82F6",
              "&:hover": { 
                backgroundColor: "#3B82F6",
                boxShadow: "none !important",
              },
              "&:active": {
                backgroundColor: "#3B82F6",
                boxShadow: "none !important",
              },
              "&:focus": {
                backgroundColor: "#3B82F6",
                boxShadow: "none !important",
              },
              borderRadius: 2,
              width: "100%",
              paddingTop: 2,
              paddingBottom: 2,
              mt: 2,
              mb: isMobile ? 2 : 4,
              textTransform: "none",
              boxShadow: "none !important",
              transition: "none !important",
              "& .MuiTouchRipple-root": {
                display: "none",
              },
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress 
                  size={20} 
                  sx={{ 
                    color: "white", 
                    mr: 1 
                  }} 
                />
              </>
            ) : (
              "TRANSFERIR"
            )}
          </Button>
        </Box>
      </Box>
    </>
  );
}

